import { useCallback } from 'react';
import { ALLOWED_KEYS } from '../constants';
import { DateSegments, Segment, SegmentRefs } from '../types';

type UseDateKeyboardNavigationParams = {
  segments: DateSegments;
  setSegments: {
    setYear: (value: string) => void;
    setMonth: (value: string) => void;
    setDay: (value: string) => void;
  };
  refs: SegmentRefs;
  onSegmentChange: (y: string, m: string, d: string) => void;
  setFocusedSegment: (segment: Segment | null) => void;
  setIsEditing: (editing: boolean) => void;
  segmentOrder: [Segment, Segment, Segment];
};

export function useDateKeyboardNavigation({
  segments,
  setSegments,
  refs,
  onSegmentChange,
  setFocusedSegment,
  setIsEditing,
  segmentOrder,
}: UseDateKeyboardNavigationParams) {
  const { year, month, day } = segments;
  const { setYear, setMonth, setDay } = setSegments;

  // Get the previous segment in order
  const getPreviousSegment = useCallback(
    (currentSegment: Segment): Segment | null => {
      const currentIndex = segmentOrder.indexOf(currentSegment);
      if (currentIndex <= 0) {
        return null;
      }
      return segmentOrder[currentIndex - 1];
    },
    [segmentOrder]
  );

  // Get the next segment in order
  const getNextSegment = useCallback(
    (currentSegment: Segment): Segment | null => {
      const currentIndex = segmentOrder.indexOf(currentSegment);
      if (currentIndex === -1 || currentIndex === segmentOrder.length - 1) {
        return null;
      }
      return segmentOrder[currentIndex + 1];
    },
    [segmentOrder]
  );

  // Helper to get segment value
  const getSegmentValue = useCallback(
    (segment: Segment): string => {
      return segments[segment];
    },
    [segments]
  );

  // Helper to set segment value
  const setSegmentValue = useCallback(
    (segment: Segment, value: string) => {
      if (segment === 'year') {
        setYear(value);
      } else if (segment === 'month') {
        setMonth(value);
      } else if (segment === 'day') {
        setDay(value);
      }
    },
    [setYear, setMonth, setDay]
  );

  // Helper to trigger onSegmentChange with updated value
  const triggerSegmentChange = useCallback(
    (segment: Segment, newValue: string) => {
      if (segment === 'year') {
        onSegmentChange(newValue, month, day);
      } else if (segment === 'month') {
        onSegmentChange(year, newValue, day);
      } else if (segment === 'day') {
        onSegmentChange(year, month, newValue);
      }
    },
    [year, month, day, onSegmentChange]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, segment: Segment) => {
      // Allow numbers (0-9)
      if (e.key >= '0' && e.key <= '9') {
        return; // Allow the default behavior
      }

      // Allow allowed keys
      if (ALLOWED_KEYS.includes(e.key as (typeof ALLOWED_KEYS)[number])) {
        // Handle special cases below
      } else {
        // Block all other keys (letters, symbols, etc.)
        e.preventDefault();
        return;
      }

      if (e.key === 'Backspace') {
        const input = e.currentTarget;
        const cursorPosition = input.selectionStart || 0;

        // If there's a selection, let default behavior handle it
        if (input.selectionStart !== input.selectionEnd) {
          return;
        }

        // If cursor is at the start (position 0)
        if (cursorPosition === 0) {
          e.preventDefault();

          // Mark as editing to prevent parent value prop from resetting segments
          setIsEditing(true);

          // Get current segment value directly from input element to avoid stale state
          const ipt = e.currentTarget;
          const currentValue = ipt.value || '';

          // If current segment has content, remove last character
          if (currentValue.length > 0) {
            let newValue = currentValue.slice(0, -1);

            // For day and month segments, remove leading zeros when backspacing
            // This ensures "31" → "3" (not "03"), "22" → "2" (not "02")
            if (segment === 'day' || segment === 'month') {
              // Remove all leading zeros - this handles cases like "02" → "" or "03" → ""
              // But also ensures "31" → "3" stays as "3" (no leading zero to remove)
              newValue = newValue.replace(/^0+/, '');
              // If it becomes empty after removing zeros, keep it empty (will show placeholder)
            }

            // Update the input value directly first, then update state
            // This prevents React's controlled component from interfering
            ipt.value = newValue;

            setSegmentValue(segment, newValue);
            triggerSegmentChange(segment, newValue);
            return;
          }

          // Current segment is empty, move to previous segment and remove its last character
          const prevSegment = getPreviousSegment(segment);
          if (prevSegment) {
            const prevValue = getSegmentValue(prevSegment);
            setFocusedSegment(prevSegment);
            refs[prevSegment].current?.focus();
            if (prevValue.length > 0) {
              let newPrevValue = prevValue.slice(0, -1);
              // Remove leading zeros for month/day
              if (prevSegment === 'day' || prevSegment === 'month') {
                newPrevValue = newPrevValue.replace(/^0+/, '');
              }
              setSegmentValue(prevSegment, newPrevValue);
              triggerSegmentChange(prevSegment, newPrevValue);
            }
          }
        }
        // If cursor is not at start, allow default behavior to delete characters normally
        // This allows users to clear the field by backspacing through all characters
      } else if (e.key === 'ArrowLeft') {
        const input = e.currentTarget;
        const cursorPosition = input.selectionStart || 0;
        const inputLength = input.value.length;

        // If cursor is at the start (position 0) or segment is empty, move to previous segment
        if (cursorPosition === 0 || inputLength === 0) {
          e.preventDefault();
          const prevSegment = getPreviousSegment(segment);
          if (prevSegment) {
            refs[prevSegment].current?.focus();
            // Move cursor to end of previous segment
            setTimeout(() => {
              const prevRef = refs[prevSegment].current;
              if (prevRef) {
                const prevLength = getSegmentValue(prevSegment).length;
                prevRef.setSelectionRange(prevLength, prevLength);
              }
            }, 0);
          }
        }
        // Otherwise, let default behavior move cursor left character by character
      } else if (e.key === 'ArrowRight') {
        const input = e.currentTarget;
        const cursorPosition = input.selectionStart || 0;
        const inputLength = input.value.length;

        // If cursor is at the end or segment is empty, move to next segment
        if (cursorPosition === inputLength || inputLength === 0) {
          e.preventDefault();
          const nextSegment = getNextSegment(segment);
          if (nextSegment) {
            refs[nextSegment].current?.focus();
            // Move cursor to start of next segment
            setTimeout(() => {
              const nextRef = refs[nextSegment].current;
              if (nextRef) {
                nextRef.setSelectionRange(0, 0);
              }
            }, 0);
          }
        }
        // Otherwise, let default behavior move cursor right character by character
      }
    },
    [
      refs,
      setFocusedSegment,
      setIsEditing,
      getPreviousSegment,
      getNextSegment,
      getSegmentValue,
      setSegmentValue,
      triggerSegmentChange,
    ]
  );

  return { onKeyDown };
}
